import { Injectable } from '@angular/core';
import { Observable, combineLatest } from 'rxjs';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';
import { Store, select } from '@ngrx/store';
import * as fromRoot from '../store';
import * as fromAuth from '../../auth/store';

export interface IMenuItem {
  type: string; // Possible values: link/dropDown/icon/separator/extLink
  name?: string; // Used as display text for item and title for separator type
  state?: string; // Router state
  icon?: string; // Material icon name (deprecated, use svgIcon instead)
  svgIcon?: string; // Path to custom SVG icon (e.g., 'assets/styles/new-theme/svg/dashboard.svg')
  tooltip?: string; // Tooltip text
  disabled?: boolean; // If true, item will not be appeared in sidenav.
  sub?: IChildItem[]; // Dropdown items
  badges?: IBadge[];
  permission?: string | string[];
  featureFlag?: string;
  subtitle?: string; // Optional subtitle for the menu item
  isClinician?: boolean;
}

export interface IMenuItem2 {
  name: string;
  svgIcon: string;
  permission?: string;
  onClick?: () => void;
}

interface IChildItem {
  type?: string;
  name: string; // Display text
  state?: string; // Router state
  icon?: string; // Material icon name (deprecated, use svgIcon instead)
  svgIcon?: string; // Path to custom SVG icon
  sub?: IChildItem[];
}

interface IBadge {
  color: string; // primary/accent/warn/hex color codes(#fff000)
  value: string; // Display text
}

/** Roles that should use the NHS-specific navigation menu. */
export const NHS_MENU_ROLES: string[] = [
  'ClientSystemAdmin',
  'ClinicalTeamAdmin',
  'SeniorManagement',
  'ClinicalServiceLead'
];

/** Roles that should use the Clinician-specific navigation menu (exclusive). */
export const CLINICIAN_MENU_ROLES: string[] = [
  'Clinician'
];

@Injectable({
  providedIn: 'root'
})
export class NavigationService {
  public sidebarRightOpen = false;
  public sidebarRightOpened = false;

  commonMenu: IMenuItem[] = [
    {
      name: 'Data & Reporting',
      subtitle: 'View trends in interactions and conversations.',
      type: 'link',
      tooltip: 'Data & Reporting',
      svgIcon: 'performance', // Using custom SVG icon
      state: 'data-reporting',
      permission: 'data-reporting.view'
    },
    {
      name: 'Dialog Designer',
      subtitle: 'View dialogs in the virtual agent.',
      type: 'link',
      tooltip: 'Dialog Designer',
      svgIcon: 'dialog-designer', // Using custom SVG icon
      state: 'dialogues',
      permission: 'dialogs.view'
    },
    {
      name: 'Conversations',
      subtitle: 'View conversations that are awaiting or have been handed over to a human.',
      type: 'link',
      tooltip: 'Conversations',
      svgIcon: 'conversations', // Using custom SVG icon
      state: 'conversations',
      permission: 'conversations.view'
    }
  ];

  // ONLY for EBO Staff
  defaultMenu: IMenuItem[] = [
    {
      name: 'Dashboard',
      type: 'link',
      tooltip: 'Dashboard',
      svgIcon: 'dashboard', // Using custom SVG icon
      state: 'dashboard'
    },
    ...this.commonMenu,
    {
      name: 'Test Virtual Agent',
      subtitle: 'Deploy dialogs and functionalities in a secure test environment.',
      type: 'link',
      tooltip: 'Test Virtual Agent',
      svgIcon: 'test-agent', // Using custom SVG icon
      state: 'chat',
      permission: 'test-agent.view'
    },
    {
      name: 'Waiting List',
      subtitle: 'View patient waiting lists across EBO healthcare clients.',
      type: 'link',
      tooltip: 'Waiting List',
      svgIcon: 'waiting-list', // Using custom SVG icon
      state: 'waiting-list',
      permission: 'waiting-list.view',
      featureFlag: 'waitingList'
    },
    {
      name: 'Client Reports',
      subtitle: 'View reports on specific activities for investigations.',
      type: 'link',
      tooltip: 'Reporting',
      svgIcon: 'reports', // Using custom SVG icon
      state: 'client-reports',
      permission: 'client-reports.view'
    },
    {
      name: 'Hangfire Jobs',
      subtitle: 'View background tasks running in parallel and monitor their status.',
      type: 'extLink',
      tooltip: 'Hangfire Jobs',
      svgIcon: 'hangfire', // Using custom SVG icon
      state: 'hangfire',
      permission: 'dev.hangfire'
    }
  ];

  // ONLY for NHS Roles
  defaultNHSMenu: IMenuItem[] = [
    {
      name: 'Dashboard',
      type: 'link',
      tooltip: 'Dashboard',
      svgIcon: 'dashboard', // Using custom SVG icon
      state: 'dashboard'
    },
    {
      name: 'Accounts Setup',
      subtitle: 'Setup profile account for all employees.',
      type: 'link',
      tooltip: 'Accounts Setup',
      svgIcon: 'accounts-setup', // Using custom SVG icon
      state: 'accounts-setup',
      permission: 'accounts-setup.view'
    },
    {
      name: 'Configurator',
      subtitle: 'View features and functionalities enabled for patients.',
      type: 'link',
      tooltip: 'Configurator',
      svgIcon: 'configurator', // Using custom SVG icon
      state: 'configurator',
      permission: 'configurator.view'
    },
    ...this.commonMenu,
    { // Not part of Common Menu for the UI - User is used it at the end of the menu
      name: 'Client Reports',
      subtitle: 'View reports on specific activities for investigations.',
      type: 'link',
      tooltip: 'Reporting',
      svgIcon: 'reports', // Using custom SVG icon
      state: 'client-reports',
      permission: 'client-reports.view'
    },
    {
      name: 'Patient interactions',
      subtitle: "View patient's feedback and comments and export session data for complaints.",
      type: 'link',
      tooltip: 'Patient interactions',
      svgIcon: 'patient-interactions', // Using custom SVG icon
      state: 'patient-interactions',
      permission: 'patient-interactions.view'
    },
    {
      name: 'Audit logs',
      subtitle: 'View and export logs of key actions by staff to investigate incidents.',
      type: 'link',
      tooltip: 'Audit logs',
      svgIcon: 'audit-logs', // Using custom SVG icon
      state: 'audit-logs',
      permission: 'audit-logs.view'
    }
  ];

  // ONLY for Clinicians
  defaultClinicianMenu: IMenuItem[] = [
    {
      name: 'Dashboard',
      type: 'link',
      tooltip: 'Dashboard',
      svgIcon: 'dashboard', // Using custom SVG icon
      state: 'dashboard'
    },
    {
      name: 'Patient Lists',
      subtitle: 'View all patients within your service and configure your case load.',
      type: 'link',
      tooltip: 'Patient Lists',
      svgIcon: 'hangfire', // Using custom SVG icon
      state: 'patient-lists'
      // permission: 'patient-lists.view'
    },
    {
      name: 'Patient Activity',
      subtitle: 'See a list of actions taken by a patient during each user session.',
      type: 'link',
      tooltip: 'Patient Activity',
      svgIcon: 'accounts-setup', // Using custom SVG icon
      state: 'patient-activity',
      permission: 'patient-activity.view'
    },
    {
      name: 'Correspondence',
      subtitle: 'Review, approve and send documents to your patients.',
      type: 'link',
      tooltip: 'Correspondence',
      svgIcon: 'correspondence', // Using custom SVG icon
      state: 'correspondence',
      permission: 'correspondence.view'
    },
    {
      name: 'Messages',
      subtitle: 'View and respond directly to patient messages and send broadcast.',
      type: 'link',
      tooltip: 'Messages',
      svgIcon: 'conversations', // Using custom SVG icon
      state: 'messages',
      permission: 'messages.view'
    },
    {
      name: 'Care Pathways',
      subtitle: 'View and edit select pathways assigned to your patients to monitor their progress.',
      type: 'link',
      tooltip: 'Care Pathways',
      svgIcon: 'care-pathways', // Using custom SVG icon
      state: 'care-pathways',
      permission: 'care-pathways.view'
    }
  ];

  // sets iconMenu as default;
  menuItems$: Observable<IMenuItem[]> = combineLatest([
    this.store.pipe(select(fromAuth.getUserRoles)),
    this.store.pipe(select(fromAuth.getEnvironment))
  ]).pipe(
    map(([roles, environment]: [string[], string]) => {
      // Select base menu according to role
      let baseMenu: IMenuItem[];
      if (Array.isArray(roles) && roles.length > 0 && roles.some(r => CLINICIAN_MENU_ROLES.includes(r))) {
        baseMenu = this.defaultClinicianMenu;
      } else if (Array.isArray(roles) && roles.length > 0 && roles.some(r => NHS_MENU_ROLES.includes(r))) {
        baseMenu = this.defaultNHSMenu;
      } else {
        baseMenu = this.defaultMenu;
      }

      // Apply environment prefix to all link-type items that have a state
      if (!environment) {
        return baseMenu;
      }
      const env = environment.toLowerCase();
      return baseMenu.map(item => {
        if (item.type === 'link' && item.state) {
          const lastSegment = item.state.split('/').pop();
          return { ...item, state: `${env}/${lastSegment}` };
        }
        return item;
      });
    }),
    distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
    shareReplay(1)
  );

  constructor(private store: Store<fromRoot.AppState>) {}
}
